import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Users, Phone, CheckCircle, XCircle, Shield, Trash2, Check } from 'lucide-react';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/home'); return; }
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
    setLoading(false);
  };

  const verifyUser = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/verify`);
      fetchUsers();
    } catch (err) {
      console.error('Failed to verify user:', err);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050108]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-12">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-6 h-6 text-[#FF007F]" />
          <h1 className="font-display text-3xl font-bold">Admin Dashboard</h1>
        </div>

        <div className="glass-card rounded-3xl p-6 mb-6">
          <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF007F]" />
            Registered Users ({users.length})
          </h2>

          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading users...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-slate-400">
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Phone</th>
                    <th className="py-3 px-4">SMS Code</th>
                    <th className="py-3 px-4">Verified</th>
                    <th className="py-3 px-4">Role</th>
                    <th className="py-3 px-4">Registered</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/3">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <img src={u.avatar} alt="" className="w-8 h-8 rounded-full" />
                          <div>
                            <p className="font-medium">{u.display_name}</p>
                            <p className="text-xs text-slate-400">@{u.username}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-1 text-slate-300">
                          <Phone className="w-3 h-3" /> {u.phone}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <code className="bg-white/10 px-2 py-1 rounded text-[#FF007F] font-mono text-sm">
                          {u.sms_code || '---'}
                        </code>
                      </td>
                      <td className="py-3 px-4">
                        {u.is_verified ? (
                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-amber-500/20 text-amber-400' : 'bg-white/10 text-slate-300'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-400 text-xs">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {!u.is_verified && u.role !== 'admin' && (
                            <button data-testid={`verify-user-${u.id}`} onClick={() => verifyUser(u.id)}
                              className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20" title="Verify manually">
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {u.role !== 'admin' && (
                            <button data-testid={`delete-user-${u.id}`} onClick={() => deleteUser(u.id)}
                              className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20" title="Delete user">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
